import React, { useEffect, useMemo, useState } from "react";
import { _ProductDetail } from "./Products";
import ProductLightbox from "./ProductLightbox";
import { renderDifficultyBadge } from "./difficulty";

interface Image {
    id: number;
    file: string;
    type: string;
}

export interface ParsedImage {
    id: number;
    file: string;
    type: string;
    subtypes: Image[];
}

interface Props {
    pDetailData: _ProductDetail;
}

const URL_PLACEHOLDER = "/img/img_placeholder.jpg";

/** Seskupí ploche pole obrázků typů [origin, main, small, max] do skupin podle hranice "origin".
 *  Hlavní URL skupiny je origin (plné rozlišení) — main_/max_/small_ jsou subtypy pro thumb/lightbox. */
function parseImages(data: Image[]): ParsedImage[] {
    const result: ParsedImage[] = [];
    let current: ParsedImage | null = null;
    for (const item of data) {
        if (item.type === "origin") {
            current = { id: item.id, file: item.file, type: "origin", subtypes: [] };
            result.push(current);
        } else if (current) {
            current.subtypes.push({ id: item.id, file: item.file, type: item.type });
        }
    }
    return result;
}

function thumbUrl(img: ParsedImage): string {
    const small = img.subtypes.find((s) => s.type === "small");
    return small?.file || img.file || URL_PLACEHOLDER;
}

function mainUrl(img: ParsedImage): string {
    const max = img.subtypes.find((s) => s.type === "max");
    return max?.file || img.file || URL_PLACEHOLDER;
}

const ProductDetailGallery: React.FC<Props> = ({ pDetailData }) => {
    const parsedImgs = useMemo(() => parseImages(pDetailData.images), [pDetailData.images]);
    const validImgs = useMemo(() => parsedImgs.filter((p) => p.file), [parsedImgs]);

    const [activeIdx, setActiveIdx] = useState(0);
    const [errored, setErrored] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    // při změně produktu reset
    useEffect(() => {
        setActiveIdx(0);
        setErrored(false);
    }, [pDetailData.id]);

    const activeImg = validImgs[activeIdx] || null;
    const activeUrl = activeImg ? mainUrl(activeImg) : URL_PLACEHOLDER;

    const onThumbClick = (e: React.MouseEvent, idx: number) => {
        e.preventDefault();
        setActiveIdx(idx);
        setErrored(false);
    };

    const lightboxImages = validImgs.map((p) => p.file);

    return (
        <div className="pd-gallery">
            <div
                className="pd-gallery__main pd-gallery-zoom"
                onClick={() => validImgs.length > 0 && setLightboxOpen(true)}
                role="button"
                tabIndex={0}
                aria-label="Zvětšit obrázek"
            >
                <img
                    src={errored ? URL_PLACEHOLDER : activeUrl}
                    onError={() => setErrored(true)}
                    alt={pDetailData.name}
                />
                <span className="pd-gallery-zoom__hint" aria-hidden="true">Zvětšit</span>
                {renderDifficultyBadge(pDetailData.difficulty)}
            </div>

            {validImgs.length > 1 && (() => {
                // Dle návrhu: max 5 dlaždic. Pokud je obrázků víc, posledních pět nahradí
                // dlaždice "+N dalších", která otevře lightbox na zbylých snímcích.
                const MAX_THUMBS = 5;
                const overflow = validImgs.length > MAX_THUMBS;
                const visible = overflow ? validImgs.slice(0, MAX_THUMBS - 1) : validImgs;
                const restCount = validImgs.length - (MAX_THUMBS - 1);
                return (
                    <ul className="pd-gallery__thumbs" aria-label="Galerie produktu">
                        {visible.map((img, idx) => (
                            <li
                                key={img.id || idx}
                                className={`pd-gallery__thumb${idx === activeIdx ? ' is-active' : ''}`}
                            >
                                <button
                                    type="button"
                                    onClick={(e) => onThumbClick(e, idx)}
                                    aria-label={`Obrázek ${idx + 1} z ${validImgs.length}`}
                                    aria-current={idx === activeIdx}
                                >
                                    <img
                                        src={thumbUrl(img)}
                                        onError={(e) => { (e.target as HTMLImageElement).src = URL_PLACEHOLDER; }}
                                        alt=""
                                        loading="lazy"
                                    />
                                </button>
                            </li>
                        ))}
                        {overflow && (
                            <li className="pd-gallery__thumb pd-gallery__thumb--more">
                                <button
                                    type="button"
                                    onClick={() => setLightboxOpen(true)}
                                    aria-label={`Zobrazit všech ${validImgs.length} obrázků`}
                                >
                                    <span className="pd-gallery__more-img">
                                        <img
                                            src={thumbUrl(validImgs[MAX_THUMBS - 1])}
                                            onError={(e) => { (e.target as HTMLImageElement).src = URL_PLACEHOLDER; }}
                                            alt=""
                                            loading="lazy"
                                        />
                                    </span>
                                    <span className="pd-gallery__more-label">+{restCount} dalších</span>
                                </button>
                            </li>
                        )}
                    </ul>
                );
            })()}

            {lightboxOpen && (
                <ProductLightbox
                    images={lightboxImages}
                    initialIndex={activeIdx}
                    onClose={() => setLightboxOpen(false)}
                    alt={pDetailData.name}
                />
            )}
        </div>
    );
};

export default ProductDetailGallery;
